import { FC, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { DiffAction, FeeConfigurationExport, FeeRuleExport } from '../../../models/fees';
import {
  ComparisonConfigDiff,
  CONFIG_COMPARABLE_FIELDS,
  PublicationComparison,
  RULE_COMPARABLE_FIELDS,
} from '../../../utils/feeComparisonDiff';
import {
  FieldDiffRow,
  TextCell,
  ValueCell,
  deleteFieldRows,
  humanizeFieldName,
  insertFieldRows,
  matchesRowSearch,
  rowClassName,
  unchangedFieldRows,
} from './diffTableHelpers';

interface ConfigRuleDiffTabProps {
  comparison: PublicationComparison | null;
  showDiffsOnly: boolean;
  jsonDiffsOnly: boolean;
}

function configRows(diff: ComparisonConfigDiff, showDiffsOnly: boolean): FieldDiffRow[] {
  const rows: FieldDiffRow[] = [];
  const label = diff.hudLine || '(unknown)';

  if (diff.action === DiffAction.Insert && diff.right) {
    rows.push(...insertFieldRows(diff.right, CONFIG_COMPARABLE_FIELDS).map((r) => ({ key: `${diff.feeConfigurationGuid}-${r.field}-insert`, hudLine: label, kind: 'insert' as const, ...r })));
    for (const rule of diff.right.rules) {
      rows.push(...insertFieldRows(rule, RULE_COMPARABLE_FIELDS).map((r) => ({ key: `${rule.ruleId}-${r.field}-insert`, hudLine: label, secondary: rule.description || rule.ruleId, kind: 'insert' as const, ...r })));
    }
    return rows;
  }
  if (diff.action === DiffAction.Delete && diff.left) {
    rows.push(...deleteFieldRows(diff.left, CONFIG_COMPARABLE_FIELDS).map((r) => ({ key: `${diff.feeConfigurationGuid}-${r.field}-delete`, hudLine: label, kind: 'delete' as const, ...r })));
    for (const rule of diff.left.rules) {
      rows.push(...deleteFieldRows(rule, RULE_COMPARABLE_FIELDS).map((r) => ({ key: `${rule.ruleId}-${r.field}-delete`, hudLine: label, secondary: rule.description || rule.ruleId, kind: 'delete' as const, ...r })));
    }
    return rows;
  }

  const ownFieldChanges = diff.changedFields.filter((f) => f !== 'rules');
  if (diff.action === DiffAction.Unchanged && ownFieldChanges.length === 0) {
    if (!showDiffsOnly && diff.left) {
      rows.push(...unchangedFieldRows(diff.left, CONFIG_COMPARABLE_FIELDS).map((r) => ({ key: `${diff.feeConfigurationGuid}-${r.field}-unchanged`, hudLine: label, kind: 'unchanged' as const, ...r })));
    }
  } else {
    for (const field of ownFieldChanges) {
      rows.push({
        key: `${diff.feeConfigurationGuid}-${field}`,
        hudLine: label,
        field: humanizeFieldName(field),
        aRaw: (diff.left as FeeConfigurationExport | null)?.[field as keyof FeeConfigurationExport],
        bRaw: (diff.right as FeeConfigurationExport | null)?.[field as keyof FeeConfigurationExport],
        kind: 'update',
      });
    }
  }

  for (const rd of diff.ruleDiffs) {
    const ruleLabel = rd.left?.description || rd.right?.description || rd.ruleId;
    if (rd.action === DiffAction.Insert && rd.right) {
      rows.push(...insertFieldRows(rd.right, RULE_COMPARABLE_FIELDS).map((r) => ({ key: `${rd.ruleId}-${r.field}-insert`, hudLine: label, secondary: ruleLabel, kind: 'insert' as const, ...r })));
    } else if (rd.action === DiffAction.Delete && rd.left) {
      rows.push(...deleteFieldRows(rd.left, RULE_COMPARABLE_FIELDS).map((r) => ({ key: `${rd.ruleId}-${r.field}-delete`, hudLine: label, secondary: ruleLabel, kind: 'delete' as const, ...r })));
    } else if (rd.action === DiffAction.Update) {
      for (const field of rd.changedFields) {
        rows.push({
          key: `${rd.ruleId}-${field}`,
          hudLine: label,
          secondary: ruleLabel,
          field: humanizeFieldName(field),
          aRaw: (rd.left as FeeRuleExport | null)?.[field as keyof FeeRuleExport],
          bRaw: (rd.right as FeeRuleExport | null)?.[field as keyof FeeRuleExport],
          kind: 'update',
        });
      }
    } else if (!showDiffsOnly && rd.left) {
      rows.push(...unchangedFieldRows(rd.left, RULE_COMPARABLE_FIELDS).map((r) => ({ key: `${rd.ruleId}-${r.field}-unchanged`, hudLine: label, secondary: ruleLabel, kind: 'unchanged' as const, ...r })));
    }
  }

  return rows;
}

export const ConfigRuleDiffTab: FC<ConfigRuleDiffTabProps> = ({ comparison, showDiffsOnly, jsonDiffsOnly }) => {
  const [first, setFirst] = useState(0);
  const [search, setSearch] = useState('');

  const rows = useMemo(
    () => (comparison?.configurationDiffs ?? []).flatMap((d) => configRows(d, showDiffsOnly)),
    [comparison, showDiffsOnly]
  );

  const filteredRows = useMemo(() => rows.filter((r) => matchesRowSearch(r, search)), [rows, search]);

  if (!comparison) {
    return <div className="text-sm text-gray-500 py-4">Select two publications to compare.</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-column align-items-center gap-2 py-6">
        <i className="pi pi-check-circle text-green-500 text-3xl" />
        <span className="text-base font-semibold">
          {showDiffsOnly ? 'No differences found' : 'No configurations on either publication'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setFirst(0);
          }}
          placeholder="Search by HUD line, rule, field, or value..."
          className="p-inputtext-sm border-1 border-solid border-gray-300"
        />
      </span>
      <DataTable
        key={String(jsonDiffsOnly)}
        value={filteredRows}
        stripedRows
        rowClassName={rowClassName}
        className="p-datatable-sm p-datatable-gridlines"
        tableStyle={{ tableLayout: 'fixed', width: '100%' }}
        emptyMessage="No results match your search"
        paginator
        rows={25}
        first={first}
        onPage={(e) => setFirst(e.first)}
        rowsPerPageOptions={[25, 50, 100]}
      >
        <Column header="HUD Line" style={{ width: '10%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <TextCell value={row.hudLine} />} />
        <Column header="Rule" style={{ width: '15%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <TextCell value={row.secondary ?? '-'} />} />
        <Column header="Field" style={{ width: '15%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <TextCell value={row.field} />} />
        <Column header="A Value" style={{ width: '30%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <ValueCell aRaw={row.aRaw} bRaw={row.bRaw} side="a" jsonDiffsOnly={jsonDiffsOnly} />} />
        <Column header="B Value" style={{ width: '30%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <ValueCell aRaw={row.aRaw} bRaw={row.bRaw} side="b" jsonDiffsOnly={jsonDiffsOnly} />} />
      </DataTable>
    </div>
  );
};

export default ConfigRuleDiffTab;
